// src/lib/landingContext.js
//
// Reads where a visitor came from, so the sign-in page can show narrative
// content matched to that source.
//
// THIS MODULE ONLY READS. The three redirect components do the capturing, each
// with its own first-touch key and its own TTL:
//
//   /s/:slug  PreviewRedirect   -> revfer_preview_ref  { slug, business_name, vertical }
//   /m        MarketingRedirect -> revfer_mktg         { utm_*, click_ids }
//   /r/:code  AffiliateRedirect -> revfer_ref          { code }
//
// Nobody lands on / directly, so there is no URL parsing here.
//
// PRECEDENCE IS FIXED, not most-recent: preview > marketing > affiliate.
// A visitor can hold several keys at once (clicked an ad in September, a
// preview link in October). Fixed order is predictable when something looks
// wrong; swapping to "whichever was captured_at most recently" is a change to
// pickSource() alone if that turns out to read better.
//
// Display precedence is NOT attribution. Each key keeps its own first-touch
// rule for crediting a conversion — this only decides what we SHOW.

const PREVIEW_KEY = 'revfer_preview_ref';
const MKTG_KEY = 'revfer_mktg';
const AFFILIATE_KEY = 'revfer_ref';

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Entries carry expires_at; treat a missing or unparseable one as live. */
function live(entry) {
  if (!entry) return false;
  if (!entry.expires_at) return true;
  try {
    return new Date(entry.expires_at).getTime() > Date.now();
  } catch {
    return true;
  }
}

// ─── Country (display only) ───────────────────────────────────────────────────
// Order: explicit ?cc= on the link > browser timezone > null.
// null means "don't know" — variants must fall back to country-neutral copy.

const SUPPORTED_COUNTRIES = ['in', 'us'];

const US_ZONES = new Set([
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'America/Detroit', 'America/Boise',
  'America/Juneau', 'America/Sitka', 'America/Nome', 'America/Menominee', 'Pacific/Honolulu',
]);
const US_ZONE_PREFIXES = ['America/Indiana/', 'America/Kentucky/', 'America/North_Dakota/'];

function countryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta') return 'in';
    if (US_ZONES.has(tz) || US_ZONE_PREFIXES.some((p) => tz.startsWith(p))) return 'us';
  } catch {
    // Intl unavailable — unknown
  }
  return null;
}

function detectCountry(mktg) {
  const explicit = mktg?.country;
  // An explicit but unsupported country (e.g. cc=gb) means "not IN/US": stay neutral,
  // don't let the timezone override what the link said.
  if (explicit) return SUPPORTED_COUNTRIES.includes(explicit) ? explicit : null;
  return countryFromTimezone();
}

function pickSource({ preview, mktg, affiliate }) {
  if (preview) return 'preview';
  if (mktg) return 'campaign';
  if (affiliate) return 'affiliate';
  return 'direct';
}

/**
 * Build the context the variant resolver reads.
 * Always returns an object, never null, so rules don't need guards.
 *
 *   {
 *     source:       'preview' | 'campaign' | 'affiliate' | 'direct',
 *     slug:         string | null,   // preview slug
 *     businessName: string | null,   // from the CTA's ?b=
 *     vertical:     string | null,   // from the CTA's ?v=, e.g. 'beauty.hair_salon'
 *     utm:          object | null,   // whole revfer_mktg entry, incl. click_ids
 *     affiliateCode:string | null,
 *     country:      'in' | 'us' | null,  // ?cc= on the link, else browser timezone, else null
 *   }
 */
export function getLandingContext() {
  const previewRaw = readJson(PREVIEW_KEY);
  const mktgRaw = readJson(MKTG_KEY);
  const affiliateRaw = readJson(AFFILIATE_KEY);

  const preview = live(previewRaw) && previewRaw.slug ? previewRaw : null;
  const mktg = live(mktgRaw) && mktgRaw.utm_source ? mktgRaw : null;
  const affiliate = live(affiliateRaw) && affiliateRaw.code ? affiliateRaw : null;

  return {
    source: pickSource({ preview, mktg, affiliate }),
    slug: preview?.slug || null,
    businessName: preview?.business_name || null,
    vertical: preview?.vertical || null,
    utm: mktg || null,
    affiliateCode: affiliate?.code || null,
    country: detectCountry(mktg),
  };
}